import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [emailLinkProfileId, setEmailLinkProfileId] = useState(null); // For email link handling
  const [isNewUser, setIsNewUser] = useState(false); // Track if user just signed up

  // Handle email link parameters on app initialization
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const profileId = urlParams.get('view_profile');
    if (profileId) {
      console.log('📧 Email link detected in AuthContext - profile ID:', profileId);
      setEmailLinkProfileId(profileId);
      // Clean up the URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // Run once on component mount

  // Set up axios interceptors for automatic token inclusion
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Auto-refresh token
  useEffect(() => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken && !token) {
      refreshTokens(refreshToken);
    } else if (token) {
      // Verify current token and get user info
      getCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const getCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      if (response.data.status === 'success') {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to get current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      if (response.data.status === 'success') {
        const { access_token, refresh_token, user } = response.data;
        
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        setToken(access_token);
        setUser(user);
        
        return { success: true, user };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const signup = async (name, email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/signup`, {
        name,
        email,
        password
      });

      if (response.data.status === 'success') {
        const { access_token, refresh_token, user } = response.data;
        
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        setToken(access_token);
        setUser(user);
        setIsNewUser(true); // Mark as new user for profile setup
        
        return { success: true, user, isNewUser: true };
      }
    } catch (error) {
      console.error('Signup failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const refreshTokens = async (refreshToken) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, null, {
        params: { refresh_token: refreshToken }
      });

      if (response.data.status === 'success') {
        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);
        setToken(access_token);
        getCurrentUser();
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const clearEmailLinkProfileId = () => {
    console.log('📧 Clearing email link profile ID');
    setEmailLinkProfileId(null);
  };

  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    signup,
    logout,
    refreshTokens,
    emailLinkProfileId,
    clearEmailLinkProfileId,
    isNewUser,
    setIsNewUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};